<template>
  <div class="download-container">
    <button 
      @click="confirmDownload" 
      class="btn btn-primary download-btn"
      :disabled="isDownloading || !cycleId"
    >
      <template v-if="!isDownloading">
        <i class="fas fa-download"></i> Download {{ cycleId ? `Cycle ${cycleId}` : 'Cycle' }} Files
      </template>
      <template v-else>
        <div class="progress-container">
          <div class="progress-circle" :style="progressStyle">
            <span v-if="showPercentage">{{ progress }}%</span>
            <i v-else class="fas fa-spinner fa-spin"></i>
          </div>
          <span class="progress-text">Downloading...</span>
        </div>
      </template>
    </button>
  </div>
</template>

<script>
import { callProtectedApi } from "../api";

export default {
  name: "DownloadCycleFolder",
  props: {
    stationId: [String, Number],
    cycleId: [String, Number],
  },
  data() {
    return {
      isDownloading: false,
      progress: 0,
      showPercentage: false,
      contentLength: 0,
      receivedLength: 0
    };
  },
  computed: {
    progressStyle() {
      const deg = (this.progress / 100) * 360;
      return {
        background: `conic-gradient(#4CAF50 ${deg}deg, #e0e0e0 ${deg}deg)`
      };
    }
  },
  methods: {
    async confirmDownload() {
      if (!this.cycleId) {
        this.$toast.warning("Please load a cycle first.");
        return;
      }
      
      const isConfirmed = window.confirm(
        `Are you sure you want to download data for cycle ${this.cycleId}?`
      );
      if (isConfirmed) {
        await this.downloadFiles();
      }
    },

    async downloadFiles() {
      this.isDownloading = true;
      this.progress = 0;
      this.receivedLength = 0;

      try {
        const response = await callProtectedApi(
          `/download-cycle/${this.stationId}/${this.cycleId}`,
          "get",
          null,
          {
            responseType: "blob",
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.lengthComputable) {
                this.contentLength = progressEvent.total;
                this.receivedLength = progressEvent.loaded;
                this.progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                this.showPercentage = true;
              } else {
                this.showPercentage = false;
                this.progress = (this.progress + 5) % 100;
              }
            }
          }
        );

        const blob = new Blob([response.data], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `station_${this.stationId}_cycle_${this.cycleId}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.error("Error downloading cycle:", error);
        this.$toast.error("Download failed. Please try again or check if the cycle has files.");
      } finally {
        this.isDownloading = false;
      }
    },
  },
};
</script>

<style scoped>
.download-container {
  display: flex;
  justify-content: center;
}

.download-btn {
  font-size: 16px;
  padding: 10px 20px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 200px;
  justify-content: center;
}

.download-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.7;
}

.progress-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  color: #333;
}

.progress-text {
  font-size: 14px;
}

.fa-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
